import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Paper, Tabs, Tab, Button
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BookIcon from '@mui/icons-material/Book';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClassById } from '../slices/classesSlice';
import { motion } from 'framer-motion';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import StudentsTab from '../components/classTabs/StudentsTab';
import ScheduleTab from '../components/classTabs/ScheduleTab';
import AttendanceTab from '../components/classTabs/attendance/AttendanceTab';
import SubjectsTab from '../components/classTabs/SubjectsTab';
import EditScheduleTab from '../components/classTabs/EditScheduleTab';

const MotionPaper = motion(Paper);

// Tab Panel Component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`class-tabpanel-${index}`}
      aria-labelledby={`class-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ViewClassPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);

  // Check if we should open a specific tab from navigation state
  useEffect(() => {
    if (location.state?.defaultTab !== undefined) {
      setTabValue(location.state.defaultTab);
      // Clear the state after using it to avoid keeping it on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch the selected class
  useEffect(() => {
    dispatch(fetchClassById(id));
  }, [id, dispatch]);

  // Access the selected class state
  const { selectedClass, selectedClassStatus, selectedClassError } = useSelector(
    (state) => state.classes
  );

  // Filter out archived students
  const activeStudents = selectedClass?.students?.filter((s) => !s.is_archived) || [];
  const archivedStudentsCount = selectedClass?.students?.filter((s) => s.is_archived).length || 0;

  // Helper function to get teacher display name
  const getTeacherName = () => {
    if (!selectedClass?.teacher) return 'غير محدد';
    
    // If teacher is an object with name properties
    if (typeof selectedClass.teacher === 'object') {
      const { first_name, last_name } = selectedClass.teacher;
      return `${first_name || ''} ${last_name || ''}`.trim() || 'غير محدد';
    }
    
    // If teacher is a string
    return selectedClass.teacher;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleExportExcelModern = () => {
    const columns = [
      { header: 'الاسم الأول', key: 'first_name' },
      { header: 'اسم العائلة', key: 'second_name' },
      { header: 'اسم ولي الأمر', key: 'parent_name' },
      { header: 'الهاتف', key: 'parent_phone' },
      { header: 'تاريخ التسجيل', key: 'date_of_registration' },
      { header: 'النقل', key: 'is_bus_joined' },
      { header: 'الصف', key: 'class_name' },
      { header: 'المعلم المسؤول', key: 'teacher' },
    ];
    const data = activeStudents.map((student) => ({
      first_name: student.first_name,
      second_name: student.second_name,
      parent_name: student.parent_name,
      parent_phone: student.parent_phone,
      date_of_registration: student.date_of_registration,
      is_bus_joined: student.is_bus_joined ? 'نعم' : 'لا',
      class_name: selectedClass?.name || '',
      teacher: getTeacherName(),
    }));
    exportToExcel(data, columns, `class_${selectedClass?.name || id}_active_students`);
  };

  const handlePrintModern = async () => {
    const columns = [
      { header: 'الاسم الأول', key: 'first_name' },
      { header: 'اسم العائلة', key: 'second_name' },
      { header: 'اسم ولي الأمر', key: 'parent_name' },
      { header: 'الهاتف', key: 'parent_phone' },
      { header: 'تاريخ التسجيل', key: 'date_of_registration' },
      { header: 'النقل', key: 'is_bus_joined' },
      { header: 'الصف', key: 'class_name' },
      { header: 'المعلم المسؤول', key: 'teacher' },
    ];
    const data = activeStudents.map((student) => ({
      first_name: student.first_name,
      second_name: student.second_name,
      parent_name: student.parent_name,
      parent_phone: student.parent_phone,
      date_of_registration: student.date_of_registration,
      is_bus_joined: student.is_bus_joined ? 'نعم' : 'لا',
      class_name: selectedClass?.name || '',
      teacher: getTeacherName(),
    }));
    await exportToPrintWindow(data, columns, `قائمة الطلاب النشطين للصف ${selectedClass?.name || ''}`);
  };

  if (selectedClassStatus === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ direction: 'rtl', bgcolor: '#F8F9FF' }}
      >
        <Typography
          align="center"
          sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', fontWeight: 700, color: '#6B7280' }}
        >
          جاري تحميل البيانات...
        </Typography>
      </Box>
    );
  }

  if (selectedClassStatus === 'failed') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ direction: 'rtl', bgcolor: '#F8F9FF' }}
      >
        <Typography
          align="center"
          sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', fontWeight: 700, color: '#DC2626' }}
        >
          {selectedClassError}
        </Typography>
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                الصف {selectedClass?.name || ''}
              </Typography>
              <Stack useFlexGap
                direction="row"
                spacing={2}
                flexWrap="wrap"
                sx={{ mt: 0.5, rowGap: 0.5 }}
              >
                <Stack useFlexGap direction="row" alignItems="center" spacing={0.5}>
                  <PersonIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.85)' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    المعلم المسؤول: {getTeacherName()}
                  </Typography>
                </Stack>
                <Stack useFlexGap direction="row" alignItems="center" spacing={0.5}>
                  <GroupIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.85)' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    الطلاب : {activeStudents.length}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/classes/edit/${id}`)}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            تعديل
          </Button>
        </MotionPaper>

        {/* Tabs */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 60,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  textTransform: 'none',
                  color: '#6B7280',
                  '&.Mui-selected': {
                    color: '#4338CA'
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  backgroundColor: '#4338CA'
                }
              }}
            >
              <Tab
                icon={<GroupIcon />}
                label="عرض الطلاب"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              {/* HIDDEN TEMPORARILY - كل شي ما عدا الطلاب */}
              {/* <Tab
                icon={<BookIcon />}
                label="عرض المواد"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<ScheduleIcon />}
                label="عرض الجدول الأسبوعي"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<EditIcon />}
                label="تعديل الجدول الأسبوعي"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<EventAvailableIcon />}
                label="عرض الحضور والغياب"
                iconPosition="start"
                sx={{ gap: 1 }}
              /> */}
            </Tabs>
          </Box>
          

          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <TabPanel value={tabValue} index={0}>
              <StudentsTab 
                activeStudents={activeStudents}
                selectedClass={selectedClass}
                handleExportExcelModern={handleExportExcelModern}
                handlePrintModern={handlePrintModern}
              />
            </TabPanel>
            
            {/* HIDDEN TEMPORARILY - كل شي ما عدا الطلاب */}
            {/* <TabPanel value={tabValue} index={1}>
              <SubjectsTab selectedClass={selectedClass} />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <ScheduleTab selectedClass={selectedClass} />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <EditScheduleTab selectedClass={selectedClass} />
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <AttendanceTab selectedClass={selectedClass} />
            </TabPanel> */}
          </Box>
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default ViewClassPage;